import { useMemo, useState } from 'react';
import { categories, memes } from '@/mocks/memes';
import SectionHeader from './SectionHeader';

export default function Gallery() {
  const [active, setActive] = useState('all');
  const [query, setQuery] = useState('');
  const [liked, setLiked] = useState<Record<string, boolean>>({});

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return memes.filter((m) => {
      const okCat = active === 'all' || m.categoryId === active;
      const okText = !q || m.title.toLowerCase().includes(q) || m.caption.toLowerCase().includes(q);
      return okCat && okText;
    });
  }, [active, query]);

  const toggleLike = (id: string) => setLiked((s) => ({ ...s, [id]: !s[id] }));

  return (
    <section id="gallery" className="scroll-mt-20 bg-background-50 py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeader
          kicker="🧰 表情包仓库"
          title="分类逛一逛，好图随便挑"
          desc="按场景挑梗图，搜一句台词或事件关键词，相中哪张直接下载、收藏、甩进群聊。"
        />

        {/* 工具栏 */}
        <div className="mb-6 flex flex-col gap-4 rounded-3xl bg-background-100 p-4 ring-1 ring-background-200 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex max-w-full gap-2 overflow-x-auto pb-1 lg:pb-0">
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setActive(c.id)}
                className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                  active === c.id ? 'bg-secondary-500 text-background-50' : 'bg-background-50 text-foreground-700 hover:bg-secondary-100'
                }`}
              >
                <span>{c.emoji}</span>
                {c.name}
                <span className={`text-xs ${active === c.id ? 'text-background-50/80' : 'text-foreground-400'}`}>{c.count}</span>
              </button>
            ))}
          </div>
          <label className="relative block lg:w-64">
            <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-400"></i>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜一句梗或事件…"
              className="w-full rounded-full border border-background-200 bg-background-50 py-2.5 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </label>
        </div>

        {/* 图库网格 */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" key={`${active}-${query}`}>
          {list.map((m) => (
            <figure key={m.id} className="group animate-pop overflow-hidden rounded-2xl bg-background-100 ring-1 ring-background-200">
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={m.url}
                  alt={m.alt}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <button
                  type="button"
                  aria-label={liked[m.id] ? '取消收藏' : '收藏表情包'}
                  onClick={() => toggleLike(m.id)}
                  className={`absolute right-2.5 top-2.5 flex h-9 w-9 items-center justify-center rounded-full backdrop-blur transition-colors ${
                    liked[m.id] ? 'bg-primary-500 text-background-50' : 'bg-black/45 text-white hover:bg-primary-500'
                  }`}
                >
                  <i className={`${liked[m.id] ? 'ri-heart-fill' : 'ri-heart-line'} text-base`}></i>
                </button>
                <div className="absolute inset-x-0 bottom-0 flex translate-y-full items-center justify-center gap-2 bg-black/55 p-2.5 backdrop-blur transition-transform duration-300 group-hover:translate-y-0">
                  <a
                    href={m.url}
                    download
                    onClick={(e) => e.preventDefault()}
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-500 px-3 py-1.5 text-xs font-bold text-background-50 hover:bg-accent-600"
                  >
                    <i className="ri-download-2-line text-sm"></i> 存图
                  </a>
                  <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold text-foreground-900">
                    <i className="ri-chat-1-line text-sm"></i> 发群
                  </span>
                </div>
              </div>
              <figcaption className="p-3.5">
                <h3 className="truncate text-sm font-bold text-foreground-900">{m.title}</h3>
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-500">{m.caption}</p>
                <div className="mt-2.5 flex items-center justify-between text-xs text-foreground-400">
                  <span className="inline-flex items-center gap-1"><i className="ri-fire-line text-primary-500"></i>{m.likes}收藏</span>
                  <span className="inline-flex items-center gap-1">{liked[m.id] ? <i className="ri-heart-fill text-primary-500"></i> : <i className="ri-heart-line"></i>}已收藏</span>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>

        {list.length === 0 && (
          <div className="rounded-3xl bg-background-100 py-16 text-center ring-1 ring-background-200">
            <p className="text-4xl">🫥</p>
            <p className="mt-3 font-bold text-foreground-800">没搜到相关的梗图</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或去“投稿 / 求图”告诉我们</p>
          </div>
        )}

        <div className="mt-10 text-center">
          <button
            type="button"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-secondary-100 px-6 py-3 text-sm font-bold text-secondary-900 transition-colors hover:bg-secondary-200"
          >
            加载更多表情包 <i className="ri-arrow-down-line"></i>
          </button>
        </div>
      </div>
    </section>
  );
}